'use client';

/**
 * UnifiedChatState — the return shape that BOTH transport hooks must satisfy.
 *
 * Two transport implementations live behind this contract:
 *   - SSE (Guide mode) — talks to the hub backend
 *   - NATS (Mingo mode) — talks to the OpenFrame backend
 *
 * The public `useChat({ mode })` hook picks one based on `mode.transport` and
 * returns this shape regardless. Consumers (MingoChat shell, advanced custom
 * embedders) work against this contract and never see which transport is live.
 *
 * Fields that only one transport can populate are typed as nullable (per-turn
 * LLM metadata is SSE-only) or optional (Guide-only message sub-fields like
 * `sources`). In the off-mode they're null/undefined.
 */

import type { ChatRef } from '../chat-ref.types';
import type { ChatAttachment } from '../utils/chat-attachment-markdown';
import type { AuthorType } from './chat.types';
import type { DialogItem } from './component.types';
import type { ChatContextItem } from './context-item.types';
import type { ChatSource, MessageSegment, ScrollAnchor } from './message.types';

// ─── Dialog-list host contract (shared by the NATS + SSE adapters) ───────────

/** Page-fetch parameters passed to a host `fetchDialogs` callback. The
 *  adapter owns the cursor — the host only resolves it against the backend.
 *  `search` is present ONLY when the adapter runs a server-side list search
 *  (never spread as an `undefined` key). */
export interface FetchDialogsParams {
  cursor?: string;
  limit?: number;
  search?: string;
}

/** Successful `fetchDialogs` response. `nextCursor: null` means "no more
 *  pages" — used to terminate the infinite-scroll observer in the list. */
export interface FetchDialogsResult {
  dialogs: DialogItem[];
  nextCursor: string | null;
}

/**
 * Dialog-management capabilities a transport exposes to `EmbeddableChat`.
 * Structurally identical to the host-injected `mingoDialogCapabilities`
 * prop — one shape, whether the list is host-owned (openframe `mingoState`)
 * or adapter-owned (`UnifiedChatState.dialogCapabilities`).
 */
export interface ChatDialogCapabilities {
  /** Show "Rename chat" in the row ⋯ menu and the conversation header. */
  canRename?: boolean;
  /** Show "Archive chat" in the row ⋯ menu and the conversation header. */
  canArchive?: boolean;
  /** Pages archived dialogs — presence gates the archive page + header button. */
  fetchArchivedDialogs?: (params: FetchDialogsParams) => Promise<FetchDialogsResult>;
  /** Restores an archived dialog — presence gates the restore button. */
  unarchiveDialog?: (id: string) => Promise<void>;
  /** Current list-search term (host/adapter owned; the list never filters). */
  searchQuery?: string;
  /** Presence wires the header magnifier + the rail's search field. */
  onSearchChange?: (query: string) => void;
  /** "Copy chat link" — the owner of the URL shape + clipboard write. */
  onCopyLink?: (dialog: DialogItem) => void;
  /**
   * When the list has settled EMPTY and is unsearched, land on the composer
   * instead of an empty "Current Chats" screen.
   *
   * A policy, not a transport detail: a public marketing panel must not make a
   * first-time visitor tap through an empty list to ask a question, while a
   * signed-in workspace panel wants the list (with its "Start New Chat"
   * affordance) as the landing surface. Omit to keep the list.
   */
  emptyListSkipsToCompose?: boolean;
}

/**
 * "This panel owns a conversation list, and no optional affordance on it."
 *
 * A frozen module singleton so it is referentially stable across renders —
 * it is read inside memo dependency lists, where a fresh `{}` would
 * invalidate them on every pass.
 */
export const EMPTY_DIALOG_CAPABILITIES: ChatDialogCapabilities = Object.freeze({});

// ─── Per-dialog token usage (Mingo backend telemetry) ────────────────────────

/**
 * Token usage snapshot for the active Mingo dialog, hydrated from the
 * backend's `dialog.tokenUsage` GraphQL field. Mirrors the shape returned
 * by the openframe `dialogs` GraphQL endpoint so hosts can pass it through
 * `fetchDialogMessages` without re-mapping.
 *
 * Null when no Mingo dialog is active, when the backend hasn't reported
 * usage yet, or when the active mode is Guide (SSE telemetry surfaces via
 * `currentInputTokens`/`currentOutputTokens` instead).
 */
export interface DialogTokenUsage {
  /** Backend chat-type discriminator, e.g. `ADMIN_AI_CHAT`. Free-form. */
  chatType?: string;
  /** Total input tokens consumed so far in this dialog. */
  inputTokensSize: number;
  /** Total output tokens emitted so far in this dialog. */
  outputTokensSize: number;
  /** Sum of input + output. May be > input + output when the backend
   *  counts cache reads in a separate bucket — trust the backend value. */
  totalTokensSize: number;
  /** Current LLM context-window occupancy in tokens — what the model
   *  has loaded right now, not the cumulative dialog sum. */
  contextSize?: number;
}

// ─── Connection state (transport-level) ──────────────────────────────────────

/**
 * High-level transport connection status. Drives any UI affordance that
 * needs to communicate "live tail is/isn't healthy" to the user. SSE/Guide
 * adapter currently reports `'connected'` whenever a turn isn't streaming
 * — the SSE side is request-response and has no long-lived socket.
 */
export type ChatConnectionState = 'connected' | 'connecting' | 'disconnected';

// ─── Streaming phase (unified across transports) ─────────────────────────────

/**
 * Granular streaming-status enum the chat shell uses to render the
 * "Thinking..."/"Streaming..." indicator above the input.
 *
 * - 'idle'      → no in-flight turn
 * - 'thinking'  → request fired; no streamed text/segments yet
 * - 'streaming' → first text/segment chunk arrived; output in progress
 *
 * Common to both Guide (SSE) and Mingo (NATS) modes — the phase
 * machine fires identically in either transport, just driven by
 * different chunk boundaries.
 */
export type StreamingPhase = 'idle' | 'thinking' | 'streaming';

// ─── Usage breakdown (SSE-only telemetry) ────────────────────────────────────

/**
 * Cross-call token-usage breakdown extracted from the SSE trailing usage
 * frame. Surfaces sub-call costs for the Haiku rewriter/classifier/summarizer
 * pipeline plus the routing decision for the main answer.
 *
 * Lives on `UnifiedChatState.currentUsageBreakdown` as `null` until the
 * server's trailing usage frame lands. Always `null` in Mingo mode — NATS
 * agent doesn't surface this telemetry.
 */
export interface UnifiedUsageBreakdown {
  haikuRewriter?: { input: number; output: number };
  haikuClassifier?: { input: number; output: number };
  haikuSummarizer?: { input: number; output: number };
  routedAnswer?: { model: string; complexity: string; thinkingBudget: number };
}

// ─── Message ─────────────────────────────────────────────────────────────────

/**
 * Generic chat message shape — unified between Guide (SSE) and Mingo (NATS).
 *
 * Guide-mode messages populate `sources` (RAG document citations);
 * Mingo-mode messages leave it undefined. Tool-execution and approval
 * events ride inside `segments` and are common to both modes.
 */
export interface UnifiedChatMessage {
  id: string;
  role: 'user' | 'assistant';

  /**
   * Optional host-supplied display name for the message author. When set, the
   * chat renders it verbatim (e.g. the signed-in user's full name for a
   * `user` bubble, or a per-message admin name from history); when omitted the
   * UI falls back to the role default (`'Mingo'` / `'You'`).
   */
  name?: string;

  /**
   * Optional host-supplied avatar URL for the message author. Used by the
   * panel to render an inline avatar on the author row (proxied via
   * `useProxiedImageUrl`). Omit/`null` to fall back to initials. For `user`
   * bubbles the avatar only renders when this is set, so consumers that don't
   * supply one keep the name-only layout.
   */
  avatar?: string | null;

  /**
   * Optional host-supplied author type — drives the name-row accent color in
   * `<ChatMessageEnhanced>` (e.g. `'admin'` renders the name in the accent
   * color, matching the standalone `/mingo` page). When omitted the panel
   * falls back to the role default (`'user'` for user bubbles, the assistant
   * type otherwise).
   */
  authorType?: AuthorType;

  /**
   * Flat string form for legacy/simple callers. The structured `segments`
   * form is preferred — it carries thinking blocks, tool calls, approval
   * cards, etc., which `<ChatMessageEnhanced>` renders.
   */
  content: string;
  segments?: MessageSegment[];

  /**
   * Highest chunk `streamSeq` consumed into THIS row. Not render input — it is
   * the per-message coverage signal `mergeHistoryWithRealtime` decides with:
   * a live synthetic is a twin of a persisted row only once history reaches
   * its seq.
   *
   * Load-bearing, not bookkeeping. Without it the merge falls through to its
   * wall-clock fallback ("created before the fetch instant ⇒ history has it"),
   * which is wrong for every turn the backend has not finished persisting: a
   * history refetch mid-stream then drops every live bubble except the single
   * `streamingMessageId`, collapsing the thread to the persisted user prompt.
   * The reducer stamps it on the rows it owns; hosts stamp the persisted value
   * on history rows.
   */
  streamSeq?: number;

  /**
   * Optional host-supplied message timestamp (creation/send time). Rendered
   * as the message-row time and, critically, fed into the memoized message's
   * equality check. Hosts SHOULD pass a stable value (the message's real
   * `createdAt`) — when omitted the panel stamps a stable per-id fallback so
   * memoization still holds. Never re-derive this as "now" per render: a
   * moving clock defeats memoization and re-renders every message (collapsing
   * open menus/cards) on each realtime chunk.
   */
  timestamp?: Date | string | number;

  /**
   * Documents this answer cited, rendered as chips beneath it. Produced by
   * BOTH transports now — SSE reads them off the per-turn metadata frame, NATS
   * decodes them out of a `GUIDE`/`SOURCES` chunk.
   */
  sources?: ChatSource[];
  /**
   * Entity references this answer's metadata described — videos and cards.
   * Data for the `[card://type:id]` markers in the body, not a render list:
   * only markers actually present in the text are rendered.
   */
  refs?: ChatRef[];

  /**
   * Per-message viewport-positioning hint. Common to both modes; the
   * message-list reads it to override default tail-on-stream behaviour
   * for a single message (e.g. long display-action answers anchor to
   * the top so the reader starts at the lede).
   */
  scrollAnchor?: ScrollAnchor;

  /**
   * When true the message is part of the conversation history but is
   * NOT rendered in the chat UI. Used for post-approval auto-continuation
   * turns where we don't want synthetic prompts polluting the visible
   * thread.
   */
  hidden?: boolean;

  /**
   * Entity-context items attached to THIS message (user bubbles only). The
   * host populates this from the outgoing send so the lib can render the
   * context chips beneath the message text (Figma node 31:28709). Undefined
   * for messages with no attached context.
   */
  contextItems?: ChatContextItem[];
}

// ─── sendMessage options ─────────────────────────────────────────────────────

/**
 * Options passed to `sendMessage`. Kept narrow on purpose — transport-specific
 * extras stay inside the transport implementation, not on the public surface.
 * Add fields here only when BOTH transports honour them.
 */
export interface UnifiedSendMessageOptions {
  /**
   * Treat the user message as background context only — added to the LLM
   * history so the model sees it, but NOT rendered in the UI. Used by hosts
   * to fire post-approval auto-continuation turns. The assistant response
   * still renders normally.
   */
  hidden?: boolean;

  /**
   * Image attachments uploaded via `useChatAttachments` to ride alongside
   * the user message. Each entry carries `{ storagePath, viewToken }` —
   * server-side the transport adapter replaces embedded view-URL markdown
   * lines with provider-native image content blocks.
   *
   * Guide/SSE mode: forwarded to the hub's `/api/docs/chat` endpoint.
   * Mingo/NATS mode: ignored (agent backend does not currently accept
   * image attachments — adapter will silently drop).
   */
  attachments?: ChatAttachment[];

  /**
   * Entity-context items the user attached via the composer's context picker
   * (the `+` "Assign Item" menu or the `@`-mention trigger). The lib collects
   * the selection and hands it to the host here on send; the host folds them
   * into its outgoing request payload (e.g. `contextItems: [{ type, id }]`).
   * Empty/undefined when the picker isn't configured or nothing is selected.
   */
  contextItems?: ChatContextItem[];
}

// ─── Return shape ────────────────────────────────────────────────────────────

/**
 * The contract every transport hook returns. Stable across SSE and NATS —
 * consumers depend on this shape and never branch on `mode` themselves.
 *
 * Mode-specific telemetry (provider, token counts, cache-hit %) is SSE-only.
 * In Mingo mode those fields are `null` because NATS-agent doesn't surface
 * per-turn LLM metadata the same way.
 */
export interface UnifiedChatState {
  // ─── Message thread ───────────────────────────────────────────────────────
  messages: UnifiedChatMessage[];

  /**
   * True while a user turn is in progress — server-side thinking, streaming,
   * or both. Driven by `streamingPhase` under the hood; exposed as a flat
   * boolean for the chat-input "submit disabled" affordance.
   */
  isLoading: boolean;

  /** Granular phase for the "Thinking..."/"Streaming..." status row above input. */
  streamingPhase: StreamingPhase;

  /** True while the adapter is rebuilding the message list from the
   *  server-side transcript store (SSE/Guide mount-time hydration). Optional —
   *  transports without server hydration (NATS manages its own dialog
   *  loading) never set it. */
  isHydratingHistory?: boolean;

  // ─── Actions ──────────────────────────────────────────────────────────────
  /** Resolution MAY carry a boolean success flag (`false` = the request
   *  failed) — consumed by batch approve-all loops to mark failed rows.
   *  Callers that only await it are unaffected (`void` = success). */
  sendMessage: (text: string, options?: UnifiedSendMessageOptions) => Promise<void | boolean>;

  /** Abort the in-flight stream. No-op when idle. */
  stopMessage: () => void;

  /** Wipe the local message buffer. Does not touch server-side history. */
  clearMessages: () => void;

  /**
   * Trigger the chat's "Discuss this row" affordance — opens a focused
   * thread scoped to a specific RAG entity. Guide-mode only; in Mingo mode
   * this is a no-op (the agent has no entity-id-filtered retrieval).
   */
  discussRef: (ref: ChatRef) => void;

  /**
   * Trigger the chat's "Display this row" affordance — same as `discussRef`
   * but for read-only card display rather than a question. Guide-mode only:
   * it dispatches a `/<cmd> display "<x>"` slash command, and only the SSE
   * transport has a slash-command registry.
   *
   * OPTIONAL, and OMITTED — not stubbed — by adapters that have no display
   * path. Consumers gate the affordance on its presence (`entity-cards`'
   * dispatcher falls back to "Ask Mingo" when it is absent), so a no-op stub
   * renders a dead "Display" row where the action that works belongs.
   */
  displayRef?: (ref: ChatRef) => void;

  // ─── Per-turn LLM metadata (Guide/SSE only) ───────────────────────────────
  /**
   * Provider key recognised by `<ModelDisplay>` for icon selection.
   * Values: 'anthropic' | 'openai' | 'google' (case-insensitive).
   * Null in Mingo mode and during the brief window before the server's
   * `message_start` frame arrives.
   */
  currentProvider: string | null;

  /** Display label for the active model, e.g. "Sonnet 4.6". Null in Mingo. */
  currentModelLabel: string | null;

  /** Model's context-window cap in tokens. Null in Mingo. */
  currentContextWindowMaxTokens: number | null;

  /** Input tokens for the current turn (from `message_start`). Null in Mingo. */
  currentInputTokens: number | null;

  /** Output tokens for the current turn (only known after stream end). */
  currentOutputTokens: number | null;

  /** Cache-hit % (read / total-input × 100). Only known after stream end. */
  currentCacheHitRatePct: number | null;

  /**
   * Cross-call token-usage breakdown (Haiku rewriter/classifier/summarizer
   * + routed-answer telemetry). Null until the SSE trailing usage frame
   * arrives. Always null in Mingo mode.
   */
  currentUsageBreakdown: UnifiedUsageBreakdown | null;

  // ─── Dialog management (Mingo: backend; Guide: localStorage) ──────────────
  //
  // All fields below are optional in practice: a transport adapter that
  // doesn't manage multi-dialog history (older `useNatsChatAdapter`
  // configs, the Guide adapter with localStorage disabled) returns the
  // empty/null defaults so existing consumers keep working unchanged.
  // Callers gate UI on `dialogs.length > 0` or `activeDialogId != null`.

  /**
   * History of dialogs available for the active mode. Mingo: paginated
   * from the openframe backend. Guide: list of recent threads from
   * localStorage. Empty when the active adapter doesn't expose dialog
   * management.
   */
  dialogs: DialogItem[];

  /** Currently-selected dialog id, or `null` when no dialog is active
   *  (draft state — "start a new conversation"). */
  activeDialogId: string | null;

  /** Switch the panel to an existing dialog. Idempotent — selecting the
   *  active id is a no-op. Pass `null` to drop back to draft state. */
  selectDialog: (id: string | null) => void;

  /** Allocate a fresh dialog on the backend (Mingo) or in localStorage
   *  (Guide) and switch to it. Returns the new dialog id. When the
   *  adapter doesn't support creation, resolves to `null`. */
  startNewDialog: () => Promise<string | null>;

  /** Delete a dialog from history. No-op (resolves) when the adapter doesn't
   *  expose `deleteDialog`.
   *
   *  REJECTS when the backend call fails — the row is kept and the error is
   *  re-thrown so a confirmation modal can stay open for a retry instead of
   *  closing over a write that never landed. Callers must `await`/`.catch()`;
   *  a bare `void state.deleteDialog(id)` produces an unhandled rejection. */
  deleteDialog: (id: string) => Promise<void>;

  /** Rename a dialog. Optimistically updates the title in the local list.
   *  No-op (resolves) when the adapter doesn't expose a rename callback.
   *
   *  ALWAYS RESOLVES — unlike `deleteDialog` / `archiveDialog`. A failure rolls
   *  the optimistic title back and is logged, never re-thrown, because
   *  `useChatDialogManager` fires rename as `void renameDialog(...)` and closes
   *  its modal immediately; rejecting here would only produce an unhandled
   *  rejection. The rollback is the user-visible signal. */
  renameDialog: (id: string, title: string) => Promise<void>;

  /** Archive a dialog (removes it from the active list). No-op (resolves) when
   *  the adapter doesn't expose an archive callback.
   *  REJECTS on failure, keeping the row — see `deleteDialog`. */
  archiveDialog: (id: string) => Promise<void>;

  /** True while the dialog list is being fetched for the first time. */
  isDialogsLoading: boolean;

  /** True when the initial dialog-list load FAILED (e.g. backend down).
   *  Lets the UI distinguish a load error from a genuinely empty list and
   *  show a retry affordance instead of the new-user empty state. */
  dialogsError: boolean;

  /** Re-run the initial dialog-list load — retry after `dialogsError`. */
  reloadDialogs: () => void;

  /** True while message history for the active dialog is being fetched. */
  isMessagesLoading: boolean;

  /** Whether more dialogs remain on the server (Mingo cursor pagination). */
  hasMoreDialogs: boolean;

  /** Fetch the next page of dialogs. No-op when `hasMoreDialogs` is false. */
  loadMoreDialogs: () => Promise<void>;

  /** Ownership scope of `dialogs` — `'my'` (current user's chats) or `'all'`
   *  (every admin's). Optional: adapters without ownership data omit it and
   *  the "My Chats / All Chats" selector stays hidden. The HOST owns the
   *  filtering — `dialogs` must already reflect this scope. */
  dialogScope?: 'my' | 'all';

  /** Switch the ownership scope — see `dialogScope`. */
  setDialogScope?: (scope: 'my' | 'all') => void;

  /** Whether more historical messages remain in the active dialog. */
  hasMoreMessages: boolean;

  /** Fetch the next page of historical messages for the active dialog. */
  loadMoreMessages: () => Promise<void>;

  /**
   * THE conversation-list signal. Present iff this state owns a conversation
   * list; absent = a single-thread panel. `EmbeddableChat` gates the whole
   * history surface — rail, stacked list, archive page, rename/archive menus,
   * header search — on its presence alone, so the panel never asks WHICH
   * transport it is talking to. Each field inside it then gates one
   * affordance, on the same rule the rest of this file uses: the presence of
   * a callback IS the capability.
   *
   * Deliberately ONE field, not a `dialogsManaged` boolean beside an object:
   * two signals derived from the same predicate can disagree, and every
   * producer would have to remember to set both.
   */
  dialogCapabilities?: ChatDialogCapabilities;

  // ─── Approval mutations (Mingo agent tool-call workflow) ──────────────────

  /** Approve an in-flight tool-call request. Errors surface via the
   *  host-supplied toast in the callback config (lib does not own UI). */
  approveRequest: (requestId: string) => Promise<void>;

  /** Reject an in-flight tool-call request. Optional `reason` is
   *  forwarded to the backend when supported. */
  rejectRequest: (requestId: string, reason?: string) => Promise<void>;

  // ─── Per-dialog token usage (Mingo only) ──────────────────────────────────

  /** Cumulative token usage for the active Mingo dialog. Null in Guide
   *  mode or when the backend hasn't reported usage yet. Per-turn
   *  metadata (`currentInputTokens` etc.) is orthogonal — that fires on
   *  every SSE `message_start`, while this hydrates from the dialog
   *  fetch + live `TOKEN_USAGE` events. */
  dialogTokenUsage: DialogTokenUsage | null;

  // ─── Connection state ─────────────────────────────────────────────────────

  /** High-level transport connection state. Drives reconnect UI in
   *  Mingo mode; always `'connected'` in Guide mode (SSE is request-
   *  response and has no persistent socket to monitor). */
  connectionState: ChatConnectionState;
}
